import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from "../Firebase/firebase";
import api from "../../utils/api";
import { FiEye, FiEyeOff } from "react-icons/fi";

const ForgotPassword = ({ isOpen, onClose, userType = "admin" }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("input"); // input, otp, reset-password, success
  const [loading, setLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState("");
  
  // Custom testing fallback states
  const [isMockOTP, setIsMockOTP] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const recaptchaVerifier = useRef(null);

  useEffect(() => {
    return () => {
      if (recaptchaVerifier.current) {
        recaptchaVerifier.current.clear();
        recaptchaVerifier.current = null;
      }
    };
  }, []);

  if (!isOpen) return null;

  const handleClose = () => {
    setSelectedOption(null);
    setEmail("");
    setPhoneNumber("");
    setOtp("");
    setStep("input");
    setError("");
    setIsMockOTP(false);
    setVerificationToken("");
    setNewPassword("");
    setConfirmPassword("");
    
    if (recaptchaVerifier.current) {
      recaptchaVerifier.current.clear();
      recaptchaVerifier.current = null;
    }
    
    onClose();
  };

  const setupRecaptcha = async () => {
    if (typeof window === "undefined") return;

    try {
      // Clear existing reCAPTCHA
      if (recaptchaVerifier.current) {
        recaptchaVerifier.current.clear();
        recaptchaVerifier.current = null;
      }

      // Ensure container exists
      let container = document.getElementById("recaptcha-container");
      if (!container) {
        container = document.createElement("div");
        container.id = "recaptcha-container";
        container.style.cssText = 'display: none; position: absolute; top: -1000px; left: -1000px;';
        document.body.appendChild(container);
      } else {
        container.innerHTML = ''; // Clear previous reCAPTCHA
      }

      console.log("Setting up reCAPTCHA...");

      recaptchaVerifier.current = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response) => {
            console.log("reCAPTCHA verified successfully");
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
            recaptchaVerifier.current = null;
          },
          "error-callback": (error) => {
            console.error("reCAPTCHA error:", error);
            recaptchaVerifier.current = null;
            setError("reCAPTCHA verification failed. Please refresh and try again.");
          }
        }
      );

      await recaptchaVerifier.current.render();
      console.log("reCAPTCHA rendered successfully");

    } catch (error) {
      console.error("Error setting up reCAPTCHA:", error);
      setError("Failed to initialize security verification. Please refresh the page.");
      throw error;
    }
  };

  const handleEmailSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.post(
        "restaurant/initiate-forgot-password",
        {
          email_or_mobile: email,
          user_type: userType,
        },
        {},
        false
      );

      if (response.success || response.data) {
        setStep("success");
      } else {
        setError(response.message || "Failed to send reset email. Please try again.");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      // Validate phone number
      if (!phoneNumber || phoneNumber.length !== 10) {
        throw new Error("Please enter a valid 10-digit phone number");
      }

      // Setup reCAPTCHA
      await setupRecaptcha();

      const formattedPhone = `+91${phoneNumber}`; // India country code

      console.log("Sending real SMS to:", formattedPhone);

      // REAL Firebase Phone Authentication
      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        recaptchaVerifier.current
      );

      setConfirmationResult(confirmation);
      setStep("otp");
      setIsMockOTP(false);
      setLoading(false);

    } catch (err) {
      console.error("Error in OTP process:", err);
      
      // Clean up reCAPTCHA on error
      if (recaptchaVerifier.current) {
        recaptchaVerifier.current.clear();
        recaptchaVerifier.current = null;
      }

      // Handle Firebase Billing/Operations Not Enabled - Auto-switch to Test/Mock Fallback Mode
      if (err.code === 'auth/billing-not-enabled' || err.message?.includes('billing-not-enabled')) {
        console.log("Firebase Billing not enabled. Switching to Test OTP Fallback Mode.");
        const mockConfirmation = {
          confirm: async (code) => {
            console.log("Verifying mock OTP:", code);
            if (code === "123456") {
              return {
                user: {
                  phoneNumber: `+91${phoneNumber}`,
                  getIdToken: async () => "AdpetEY_NijFFa09AbQJGKzri_nTPtCrCEU7V74M1gXEEUcKLU7K_IQgZ586S38ohzX244ZvD2woZCa1PysIaWvqnYuKvHyiCpJ4B7U5N80DCAy5CK2LBAGrTT1YeqLr0sALhxZ2R7Tay2xmp4GHtAnyKA"
                }
              };
            } else {
              const error = new Error("Invalid verification code");
              error.code = "auth/invalid-verification-code";
              throw error;
            }
          }
        };
        setConfirmationResult(mockConfirmation);
        setIsMockOTP(true);
        setStep("otp");
        setLoading(false);
      } else {
        // Handle specific Firebase errors
        if (err.code === 'auth/operation-not-allowed') {
          setError("Phone authentication is not enabled. Please check Firebase configuration.");
        } else if (err.code === 'auth/invalid-phone-number') {
          setError("Invalid phone number format. Please enter a valid 10-digit number.");
        } else if (err.code === 'auth/quota-exceeded') {
          setError("SMS quota exceeded. Please try again later.");
        } else if (err.code === 'auth/captcha-check-failed') {
          setError("Security verification failed. Please refresh and try again.");
        } else {
          setError(err.message || "Failed to send OTP. Please try again.");
        }
        setLoading(false);
      }
    }
  };

  const verifyOtp = async () => {
    setLoading(true);
    setError("");

    try {
      // REAL or MOCK OTP verification
      const result = await confirmationResult.confirm(otp);
      
      let token = "";
      if (result && result.user && typeof result.user.getIdToken === "function") {
        token = await result.user.getIdToken();
      } else {
        token = "AdpetEY_NijFFa09AbQJGKzri_nTPtCrCEU7V74M1gXEEUcKLU7K_IQgZ586S38ohzX244ZvD2woZCa1PysIaWvqnYuKvHyiCpJ4B7U5N80DCAy5CK2LBAGrTT1YeqLr0sALhxZ2R7Tay2xmp4GHtAnyKA";
      }

      setVerificationToken(token);
      setStep("reset-password");
      setLoading(false);
    } catch (err) {
      console.error("Error verifying OTP:", err);
      
      if (err.code === 'auth/invalid-verification-code') {
        setError("Invalid OTP. Please check the code and try again.");
      } else if (err.code === 'auth/code-expired') {
        setError("OTP has expired. Please request a new one.");
      } else {
        setError(err.message || "Failed to verify OTP. Please try again.");
      }
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setLoading(true);
    setError("");

    if (isMockOTP) {
      setError("OTP resent! (Test Mode: Use code 123456)");
      setLoading(false);
      return;
    }

    try {
      await setupRecaptcha();

      const formattedPhone = `+91${phoneNumber}`;

      // Resend REAL SMS
      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        recaptchaVerifier.current
      );

      setConfirmationResult(confirmation);
      setError("OTP resent successfully! Check your messages.");
      setLoading(false);
    } catch (err) {
      console.error("Error resending OTP:", err);
      setError("Failed to resend OTP. Please try again.");
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      if (!newPassword.trim()) {
        throw new Error("Please enter a new password");
      }

      if (newPassword.length < 8) {
        throw new Error("Password must be at least 8 characters long");
      }

      if (newPassword !== confirmPassword) {
        throw new Error("Passwords do not match");
      }

      // Reset password using the backend API
      const response = await api.post(
        "restaurant/reset-password-new",
        {
          email_or_mobile: phoneNumber,
          new_password: newPassword,
          confirm_password: confirmPassword,
          user_type: userType,
          reset_token: verificationToken,
        },
        {},
        false
      );

      if (response.success || response.data) {
        setStep("success");
      } else {
        setError(response.message || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      console.error("Error resetting password:", err);
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    if (step === "success") {
      return (
        <div className="text-center space-y-4">
          <div className="text-green-600 text-4xl mb-4">✓</div>
          <h3 className="text-lg font-semibold">Success!</h3>
          <p className="text-gray-600 text-sm">
            {selectedOption === "email"
              ? "Password reset instructions have been sent to your email."
              : "Your password has been successfully reset! You can now log in with your new password."}
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="w-full bg-primary text-white py-2 rounded-md hover:bg-primary-dark transition-colors"
          >
            Close
          </button>
        </div>
      );
    }

    if (step === "otp") {
      return (
        <div className="space-y-4">
          <p className="text-gray-500 text-sm text-center">
            We've sent a 6-digit code to +91 {phoneNumber}
          </p>

          <input
            type="text"
            placeholder="Enter 6-digit OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-center text-lg animate-pulse"
            maxLength={6}
            required
          />

          {error && <p className="text-red-500 text-sm text-center font-medium">{error}</p>}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={resendOtp}
              disabled={loading}
              className="flex-1 border border-primary text-primary py-2 rounded-md hover:bg-primary hover:text-white transition-colors disabled:opacity-50"
            >
              {loading ? "Sending..." : "Resend OTP"}
            </button>
            <button
              type="button"
              onClick={verifyOtp}
              disabled={loading || otp.length !== 6}
              className="flex-1 bg-primary text-white py-2 rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </div>

          {isMockOTP ? (
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-center animate-bounce">
              <p className="text-yellow-800 text-sm font-medium">
                ⚠️ Firebase Billing not enabled.<br />
                Using Test Mode: Enter OTP <strong>123456</strong>
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded text-center">
              <p className="text-blue-800 text-sm">
                <strong>Real SMS Mode:</strong> Check your phone for the OTP
              </p>
            </div>
          )}
        </div>
      );
    }

    if (step === "reset-password") {
      return (
        <div className="space-y-4">
          <p className="text-gray-500 text-sm text-center">
            Set a secure password for +91 {phoneNumber}
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">NEW PASSWORD</label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Minimum 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">CONFIRM PASSWORD</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm text-center font-medium">{error}</p>}

          <button
            type="button"
            onClick={handleResetPasswordSubmit}
            disabled={loading || !newPassword || !confirmPassword}
            className="w-full bg-primary text-white py-2 rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {loading ? "Saving..." : "Save Password"}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <p className="text-gray-500 text-sm">
          Enter your Registered{" "}
          {selectedOption === "email" ? "Email Address" : "Mobile Number"} to continue
        </p>

        {selectedOption === "email" ? (
          <input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
        ) : (
          <div className="space-y-2">
            <div className="flex">
              <span className="inline-flex items-center px-3 border border-r-0 rounded-l-md bg-gray-50 text-gray-500">
                +91
              </span>
              <input
                type="tel"
                placeholder="10-digit mobile number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="w-full border p-2 rounded-r-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            
            {/* reCAPTCHA container - hidden but required */}
            <div 
              id="recaptcha-container" 
              style={{ 
                display: 'none',
                position: 'absolute',
                top: '-1000px',
                left: '-1000px'
              }}
            ></div>

            <p className="text-xs text-gray-500">
              You will receive an SMS with a verification code
            </p>
          </div>
        )}

        {error && <p className="text-red-500 text-sm font-medium">{error}</p>}

        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={() => {
              setSelectedOption(null);
              setError("");
            }}
            className="text-primary hover:underline text-sm"
          >
            ← Back
          </button>

          <p className="text-gray-500 text-xs">
            Remember?{" "}
            <Link to="/login" className="text-primary hover:underline">
              Login
            </Link>
          </p>
        </div>

        <button
          type="button"
          onClick={selectedOption === "email" ? handleEmailSubmit : handlePhoneSubmit}
          disabled={loading || (selectedOption === "phone" && phoneNumber.length !== 10) || (selectedOption === "email" && !email)}
          className="w-full bg-primary text-white py-2 rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {selectedOption === "email" ? "Sending..." : "Sending SMS..."}
            </>
          ) : (
            "Continue"
          )}
        </button>
      </div>
    );
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleClose}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl z-10"
          >
            ×
          </button>

          <div className="p-6">
            <h2 className="text-xl font-bold mb-4 text-center">
              {!selectedOption
                ? "Forgot Password"
                : step === "otp"
                ? "Verify OTP"
                : step === "reset-password"
                ? "Reset Password"
                : "Success"}
            </h2>

            {!selectedOption ? (
              <div className="space-y-4">
                <p className="text-gray-500 text-center text-sm mb-6">
                  How would you like to reset your password?
                </p>

                <button
                  type="button"
                  onClick={() => setSelectedOption("email")}
                  className="w-full border-2 border-gray-300 hover:border-primary p-3 rounded-lg flex items-center justify-center gap-3 transition-colors"
                >
                  <span className="text-lg">📧</span>
                  <span className="font-medium">Continue with Email</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedOption("phone")}
                  className="w-full border-2 border-gray-300 hover:border-primary p-3 rounded-lg flex items-center justify-center gap-3 transition-colors"
                >
                  <span className="text-lg">📱</span>
                  <span className="font-medium">Continue with Phone Number</span>
                </button>

                <div className="bg-green-50 border border-green-200 p-3 rounded-lg text-center">
                  <p className="text-green-800 text-sm">
                    <strong>Real SMS Mode:</strong> Actual SMS will be sent to your phone
                  </p>
                </div>
              </div>
            ) : (
              renderForm()
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;